/**
 * i18n dictionary of the "chrome" for the excursions silo (fiche + hub).
 *
 * Why: the fiche sections (Highlights, Itinerary, Practical, FAQ…) and the hub
 * carried hard-coded French labels and intro copy. To make the EN version 100%
 * English WITHOUT duplicating components, each section reads its labels here via
 * `locale`. Components keep `locale = "fr"` by default → zero FR regression
 * (FR strings are lifted byte-for-byte from the original markup).
 *
 * Holds ONLY reusable interface labels + fallback content (default FAQ / related
 * links). Page-specific content lives in the slugs (slugs/ for FR, slugs-en/ for
 * EN). See ExcursionSlugView and the shared hub.
 */
import { type I18n } from "@/config/localeText";

export type ExcursionUi = {
  // HighlightsSection
  highlightsIntro: string;
  highlightsSrPrefix: string;
  // ItinerarySection
  itineraryIntro: string;
  // PracticalInfoSection
  practicalIntro: string;
  // Fiche page chrome (overview / FAQ / see also)
  overviewHeading: string;
  overviewMainAlt: string;
  overviewComfortAlt: string;
  faqHeading: string;
  seeAlsoHeading: string;
  // Breadcrumbs
  crumbHome: string;
  crumbExcursions: string;
  // Not-found fallback
  notFoundTitle: string;
  notFoundBack: string;
  // Defaults (used when a fiche has no faq / relatedLinks of its own)
  fallbackFaq: { question: string; answer: string }[];
  relatedDefault: { label: string; href: string }[];
  // Locale-aware hrefs
  homeHref: string;
  excursionsHref: string;
};

export const EXCURSION_UI: I18n<ExcursionUi> = {
  fr: {
    highlightsIntro:
      "Les points clés de cette excursion pour cadrer votre départ, vos arrêts et le rythme de visite avec votre chauffeur privé.",
    highlightsSrPrefix: "Points forts de l’excursion: ",
    itineraryIntro:
      "Exemple de déroulé pour visualiser les étapes de la journée, les temps de trajet et les pauses possibles selon votre programme.",
    practicalIntro:
      "Informations clés pour préparer votre sortie : durée, points de départ, conseils pratiques et options additionnelles.",
    overviewHeading: "Aperçu concret de l'excursion",
    overviewMainAlt: "Lieu principal de l'excursion",
    overviewComfortAlt: "Confort passager en trajet",
    faqHeading: "Questions fréquentes",
    seeAlsoHeading: "Voir aussi",
    crumbHome: "Accueil",
    crumbExcursions: "Excursions",
    notFoundTitle: "Excursion introuvable",
    notFoundBack: "Retour aux excursions",
    fallbackFaq: [
      {
        question: "Puis-je personnaliser l'itinéraire de cette excursion ?",
        answer:
          "Oui, nos chauffeurs adaptent les arrêts et la durée selon votre rythme, dans les limites horaires choisies.",
      },
      {
        question:
          "Cette excursion est-elle disponible avec prise en charge hôtel ?",
        answer:
          "Oui, la prise en charge est possible depuis Cannes et les communes voisines selon disponibilité.",
      },
    ],
    relatedDefault: [
      {
        label: "Chauffeur privé pour transferts",
        href: "/chauffeur/transferts",
      },
      { label: "Location de véhicules premium", href: "/location/vehicules" },
      { label: "Réserver un trajet", href: "/reservation" },
    ],
    homeHref: "/",
    excursionsHref: "/excursions",
  },
  en: {
    highlightsIntro:
      "The key points of this excursion to frame your departure, your stops and the pace of visit with your private chauffeur.",
    highlightsSrPrefix: "Excursion highlights: ",
    itineraryIntro:
      "A sample run-through to picture the steps of the day, the travel times and the possible breaks depending on your programme.",
    practicalIntro:
      "Key information to prepare your outing: duration, pick-up points, practical tips and additional options.",
    overviewHeading: "A concrete glimpse of the excursion",
    overviewMainAlt: "Main location of the excursion",
    overviewComfortAlt: "Passenger comfort on the journey",
    faqHeading: "Frequently asked questions",
    seeAlsoHeading: "See also",
    crumbHome: "Home",
    crumbExcursions: "Excursions",
    notFoundTitle: "Excursion not found",
    notFoundBack: "Back to excursions",
    fallbackFaq: [
      {
        question: "Can I customise the itinerary of this excursion?",
        answer:
          "Yes, our chauffeurs adapt the stops and the duration to your pace, within the chosen time slot.",
      },
      {
        question: "Is this excursion available with hotel pick-up?",
        answer:
          "Yes, pick-up is possible from Cannes and the neighbouring towns subject to availability.",
      },
    ],
    relatedDefault: [
      {
        label: "Private chauffeur for transfers",
        href: "/en/chauffeur/transfers",
      },
      {
        label: "Premium vehicle rental",
        href: "/en/location/vehicules",
      },
      { label: "Book a trip", href: "/en/reservation" },
    ],
    homeHref: "/",
    excursionsHref: "/en/excursions",
  },
  ar: {
    highlightsIntro:
      "النقاط الأساسية لهذه الرحلة لتنظيم انطلاقك ووقفاتك وإيقاع الزيارة مع سائقك الخاص.",
    highlightsSrPrefix: "أبرز معالم الرحلة: ",
    itineraryIntro:
      "نموذج لسير اليوم لتصوّر مراحل الرحلة وأوقات التنقّل والاستراحات الممكنة حسب برنامجك.",
    practicalIntro:
      "معلومات أساسية لتحضير خرجتك: المدة ونقاط الانطلاق ونصائح عملية وخيارات إضافية.",
    overviewHeading: "لمحة ملموسة عن الرحلة",
    overviewMainAlt: "الموقع الرئيسي للرحلة",
    overviewComfortAlt: "راحة الركّاب أثناء التنقّل",
    faqHeading: "الأسئلة الشائعة",
    seeAlsoHeading: "اطّلع أيضًا",
    crumbHome: "الرئيسية",
    crumbExcursions: "الرحلات",
    notFoundTitle: "الرحلة غير موجودة",
    notFoundBack: "العودة إلى الرحلات",
    fallbackFaq: [
      {
        question: "هل يمكنني تخصيص مسار هذه الرحلة؟",
        answer:
          "نعم، يكيّف سائقونا الوقفات والمدة حسب إيقاعك، ضمن الحدود الزمنية المختارة.",
      },
      {
        question: "هل هذه الرحلة متاحة مع الاصطحاب من الفندق؟",
        answer: "نعم، الاصطحاب ممكن من كان والبلدات المجاورة حسب التوفّر.",
      },
    ],
    relatedDefault: [
      {
        label: "سائق خاص لخدمات النقل",
        href: "/ar/chauffeur/transferts",
      },
      {
        label: "تأجير سيارات فاخرة",
        href: "/ar/location/vehicules",
      },
      { label: "احجز رحلة", href: "/ar/reservation" },
    ],
    homeHref: "/ar",
    excursionsHref: "/ar/excursions",
  },
};
